<template lang="pug">
div
  v-row(:no-gutters="$vuetify.breakpoint.mobile")
    v-col(cols="12" sm="6" :class="[{ 'mb-2':$vuetify.breakpoint.mobile }]")
      stat-card(title="Potential sales from appointments" :value="salesTrends.potential_sales | displayPriceNoCent" :loading="isLoading")
    v-col(cols="12" sm="6")
      stat-card(title="Missed sales from appointments" :value="salesTrends.missed_sales | displayPriceNoCent" :loading="isLoading")

  v-row.mb-4
    v-col(cols="12" sm="4")
      comparison-card(title="Total " averageTitle="Total appointments comparison" subject="Arrived appointments" chartSubject="appointments" :todayValue="stats.total || 0" :yesterdayValue="prevStats.total || 0" category="appointment" :loading="isFetchingTrends" :isToday="isToday" :period="currentDateRange")
    v-col(cols="12" sm="4")
      comparison-card(title="Arrived appointments" averageTitle="Arrived appointments" subject="Arrived appointments" chartSubject="arrived" :todayValue="stats.arrived || 0" :yesterdayValue="prevStats.arrived || 0" category="appointment" :loading="isFetchingTrends" :isToday="isToday" :period="currentDateRange")
    v-col(cols="12" sm="4")
      comparison-card(title="Cancelled appointments" averageTitle="Cancelled appointments" subject="Cancelled appointments" chartSubject="cancelled" :todayValue="stats.cancelled || 0" :yesterdayValue="prevStats.cancelled || 0" category="appointment" :loading="isFetchingTrends" :isToday="isToday" :period="currentDateRange")
    v-col(cols="12" sm="4")
      comparison-card(title="No-show appointments" averageTitle="No-show appointments" subject="No-show appointments" chartSubject="no-show" :todayValue="stats.no_show || 0" :yesterdayValue="prevStats.no_show || 0" category="appointment" :loading="isFetchingTrends" :isToday="isToday" :period="currentDateRange")
    v-col(cols="12" sm="4")
      comparison-card(title="Rescheduled appointments" averageTitle="Rescheduled appointments" subject="Rescheduled appointments" chartSubject="rescheduled" :todayValue="stats.rescheduled || 0"  :yesterdayValue="prevStats.rescheduled || 0" category="appointment" :loading="isFetchingTrends" :isToday="isToday" :period="currentDateRange")

  v-card.rounded-lg.pa-0.pa-md-6.mb-4(color="white" flat isFetchingTrends)
    div.px-6(v-if="isFetchingTrends")
      v-skeleton-loader(type="table-row-divider@3")
    div(v-else)
      .text-lg.font-semibold.pa-6.pa-md-0(v-if="isEmptyChartData") Appointment trend
      v-row
        v-col(v-if="!isEmptyChartData" cols="12" md="4")
          div.px-6.px-md-0
            .text-lg.font-semibold.my-3 Appointment trend
            .mb-1 For {{ periodText }},
            ul
              li Your
                span.font-semibold highest
                span {{ overviewHighest }}
                span at
                span.font-semibold {{ overview.max_total }}
                span {{ overview.max_total > 0 ? 'patients' : 'patient' }}
              li Your
                span.font-semibold lowest
                span {{ overviewLowest }}
                span at
                span.font-semibold {{ overview.min_total }}
                span {{ overview.min_total > 0 ? 'patients' : 'patient' }}
              li Your
                span.font-semibold average
                span appointments is
                span.font-semibold {{ overview.avg_total | toDecimalPlaces(2) }}
                span {{ overview.avg_total > 0 ? 'patients' : 'patient' }}
        v-col(cols="12" :md="isEmptyChartData ? '12' : '8'")
          v-card(flat)
            apexchart(type="bar" height="300" :options="chartOptions" :series="chartOptions.series")
            v-overlay.primary--text.text-center(v-if="isEmptyChartData" absolute color="white" opacity="0.9" z-index="2")
              v-icon.mb-2(color="primary" size=20) $yzi-bar-chart-10
              .h8.mb-1 No data available
              .text-sm There’s no data available yet for this chart

  v-row(v-if="!isToday && !isLoading")
    v-col(cols="12" sm="4")
      stat-card(title="Total booking fees" :value="bookingFees.total | displayPriceNoCent")
    v-col(cols="12" sm="4")
      stat-card(title="Booking fees collected" :value="bookingFees.collected | displayPriceNoCent")
    v-col(cols="12" sm="4")
      stat-card(title="Booking fees due" :value="bookingFees.due | displayPriceNoCent")
    v-col(cols="12" sm="6")
      chart-card(:loading="isFetchingDailyTrends" title="Average daily patients" :chartLabels="dailyLabels" :chartValues="dailyValues" :timeframe="timeframe" type="custom" :currentDateRange="currentDateRange" :isToday="isToday" :colors="['#E91E63']")
    v-col(cols="12" sm="6")
      chart-card(:loading="isFetchingHourlyTrends" title="Average hourly patients" :chartLabels="hourlyLabels" :chartValues="hourlyValues" :timeframe="timeframe" type="custom" :currentDateRange="currentDateRange" :isToday="isToday" :colors="['#E91E63']")
    v-col(cols="12" sm="4")
      pie-chart-card(:loading="isFetchingSegments" title="Booking channels" type="appointments" :data="bookingChannelsData")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import { rangeValueFromPeriod } from '@/utils'
import ComparisonCard from './ComparisonCard.vue'
import StatCard from './StatCard.vue'
import ChartCard from './ChartCard.vue'
import PieChartCard from './PieChartCard.vue'

export default {
  name: 'AppointmentSection',
  components: { ComparisonCard, StatCard, ChartCard, PieChartCard },
  props: {
    isToday: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isLoading: true,
    isFetchingTrends: true,
    isFetchingSegments: true,
    isFetchingDailyTrends: true,
    isFetchingHourlyTrends: true,
    timeframe: null,
    trends: {},
    salesTrends: {},
    bookingFees: {},
    stats: {},
    prevStats: {},
    bookingChannelsData: {},
    chartLabels: [],
    chartValues: [],
    dailyValues: [],
    dailyLabels: [],
    hourlyValues: [],
    hourlyLabels: [],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      dates: 'insight/getDates',
      beginDate: 'insight/getBeginDate',
      untilDate: 'insight/getUntilDate',
      selectedProviders: 'insight/getSelectedProviders',
      selectedCompanies: 'insight/getSelectedCompanies',
      currentDateRange: 'insight/getCurrentDateRange',
      isHq: 'hq/getIsHq'
    }),
    overview () {
      return this.trends.trend?.overview ?? { max_sales: 0, min_sales: 0, avg_sales: 0 }
    },
    isEmptyChartData () {
      return this.chartValues.every(item => item === 0)
    },
    chartOptions () {
      return {
        series: [{
          data: this.chartValues,
          name: '',
        }],
        colors: ['#E91E63'],
        chart: {
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: this.chartLabels,
          labels: {
            formatter: (value) => {
              return this.dateFormatFromTimeFrame(value, false)
            }
          },
        },
        tooltip: {
          x: {
            formatter: (value) => {
              const date = DateTime.fromISO(value)
              switch (this.timeframe) {
                case 'monthly':
                  return date.toFormat('MMM yyyy')
                case 'hourly':
                  return date.toFormat('h a, d MMM yyyy')
                default:
                  return date.toFormat('d MMM yyyy')
              }
            }
          }
        }
      }
    },
    periodText () {
      if (this.isToday) {
        return 'today'
      }
      return (this.currentDateRange?.name ?? '').toLowerCase()
    },
    overviewHighest () {
      const max = this.overview.max_total || 0
      return this.labelFromValue(max)
    },
    overviewLowest () {
      const min = this.overview.min_total || 0
      return this.labelFromValue(min)
    },
  },
  watch: {
    dates () {
      this.fetchData()
    },
    selectedProviders: {
      handler() {
        this.fetchData()
      },
      deep: true
    },
    selectedCompanies: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),
    labelFromValue (value) {
      const index = this.chartValues.findIndex(i => i === value)
      if (index >= 0 && value > 0) {
        const date = this.chartLabels[index]
        const formattedDate = this.dateFormatFromTimeFrame(date, true)
        return `appointments is on ${formattedDate},`
      }
      return 'appointments is'
    },
    dateFormatFromTimeFrame (value, showDetail) {
      const date = DateTime.fromISO(value)
      switch (this.timeframe) {
        case 'monthly':
          return date.toFormat('MMM')
        case 'daily':
          return date.toFormat(showDetail ? 'd MMM' : 'd')
        case 'hourly':
          return date.toFormat('h a')
        default:
          return date.toFormat('h')
      }
    },
    fetchData() {
      this.fetchTrends()
      this.fetchSalesTrends()
      this.fetchSegments()
      this.fetchDailyTrends()
      this.fetchHourlyTrends()
    },
    fetchSalesTrends (stopRefetch) {
      this.isLoading = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/appointments/sales`, { params })
        .then(resp => {
          this.salesTrends = resp.data
          this.bookingFees = resp.data.booking_fees
          this.isLoading = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchSalesTrends(true)
          } else {
            this.isLoading = false
          }
        })
    },
    fetchTrends (stopRefetch) {
      this.isFetchingTrends = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/appointments`, { params })
        .then(resp => {
          const data = resp.data
          this.trends = data
          this.stats = data.stats
          this.prevStats = data.prev_stats
          this.updateChart()
          this.isFetchingTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchTrends(true)
          } else {
            this.isFetchingTrends = false
          }
        })
    },
    fetchSegments (stopRefetch) {
      this.isFetchingSegments = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/appointments/segments`, { params })
        .then(resp => {
          const booking = resp.data?.booking_channels
          if (booking) {
            this.bookingChannelsData = {
              labels: ['Booked by patients', 'Booked by staff'],
              values: [booking.page, booking.admin],
            }
          }
          this.isFetchingSegments = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchSegments(true)
          } else {
            this.isFetchingSegments = false
          }
        })
    },
    fetchDailyTrends (stopRefetch) {
      this.isFetchingDailyTrends = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/appointments/daily`, { params })
        .then(resp => {
          const stats = resp.data.stats
          this.dailyLabels = stats.map(x => x.day)
          this.dailyValues = stats.map(x => x.avg)
          this.isFetchingDailyTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchDailyTrends(true)
          } else {
            this.isFetchingDailyTrends = false
          }
        })
    },
    fetchHourlyTrends (stopRefetch) {
      this.isFetchingHourlyTrends = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/appointments/hourly`, { params })
        .then(resp => {
          const stats = resp.data.stats
          this.hourlyLabels = stats.map(x => x.hour)
          this.hourlyValues = stats.map(x => x.avg)
          this.isFetchingHourlyTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchHourlyTrends(true)
          } else {
            this.isFetchingHourlyTrends = false
          }
        })
    },
    updateChart () {
      const trend = this.trends.trend
      this.chartLabels = trend.points.map(x => x.point)
      this.chartValues = trend.points.map(i => i.data.total)
      this.timeframe = trend.timeframe
    },
  }
}
</script>